import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { GitBranch, Network, ShieldAlert, Terminal, Workflow } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { usePilotContext } from '../hooks/usePilotContext';
import MagicBento from '../components/MagicBento';
import { PIPELINE_BENTO_CARDS, PIPELINE_BENTO_GLOW } from '../data/magicBentoPresets';

/** UI label: orchestration is automated; offensive steps are approval-gated (not full autonomy). */
const PIPELINE_PAGE_TITLE = 'Autonomous pipeline';

export function AutonomousPipelinePage() {
  const { context, loading: pilotLoading } = usePilotContext();
  const allowlistBlocksExecutor = context.network_scan_allowlist_gate_open === false;
  const [config, setConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [dojo, setDojo] = useState(null);
  const [latest, setLatest] = useState(null);
  const [busy, setBusy] = useState(false);
  const [execTarget, setExecTarget] = useState('');
  const [execProfile, setExecProfile] = useState('nmap_tcp_default');
  const [execAutoRisk, setExecAutoRisk] = useState(true);
  const [execInfo, setExecInfo] = useState(null);
  const [kaliStatus, setKaliStatus] = useState(null);
  const [graphSummary, setGraphSummary] = useState(null);
  const [codefixTitle, setCodefixTitle] = useState('');
  const [codefixSummary, setCodefixSummary] = useState('');
  const [snapshotBusy, setSnapshotBusy] = useState(false);

  const load = async () => {
    setConfigLoading(true);
    try {
      const [c, d, l, ex, rk, gs] = await Promise.all([
        api.get('/api/autonomous-pipeline/config').catch(() => null),
        api.get('/api/autonomous-pipeline/integrations/dojo').catch(() => null),
        context.customer_id
          ? api.get(`/api/autonomous-pipeline/runs/${context.customer_id}/latest`).catch(() => null)
          : Promise.resolve(null),
        api.get('/api/pentest-executor/profiles').catch(() => null),
        api.get('/api/aertax-kali/status').catch(() => null),
        api.get('/api/graph/findings/summary').catch(() => null),
      ]);
      setConfig(c);
      setDojo(d);
      setLatest(l?.run || null);
      setExecInfo(ex);
      setKaliStatus(rk);
      setGraphSummary(gs);
    } catch (e) {
      console.error(e);
    } finally {
      setConfigLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [context.customer_id]);

  const runEngagement = async () => {
    if (!context.customer_id) return;
    setBusy(true);
    try {
      const res = await api.post(`/api/autonomous-pipeline/runs/${context.customer_id}`, {
        mode: 'engagement',
        stealth: true,
      });
      toast.success(`Engagement pipeline ${res.run_id?.slice(0, 8)}…`);
      setLatest(res.run);
    } catch (e) {
      toast.error(e.message || 'Run failed');
    } finally {
      setBusy(false);
    }
  };

  const runAllowlistedScan = async () => {
    if (!context.customer_id || !execTarget.trim()) return;
    setBusy(true);
    try {
      const res = await api.post(`/api/pentest-executor/${context.customer_id}/run`, {
        profile: execProfile,
        targets: [execTarget.trim()],
        ingest_assets: true,
        auto_risk_scan: execAutoRisk,
      });
      toast.success(
        <div className="space-y-1">
          <div>Executor scan {res.scan_id} — {res.devices_found} host(s) with open ports</div>
          <Link
            to={`/runs/${encodeURIComponent(res.scan_id)}`}
            className="text-xs text-primary underline"
          >
            View run details
          </Link>
        </div>
      );
      setLatest(null);
      await load();
    } catch (e) {
      toast.error(e.message || 'Executor failed');
    } finally {
      setBusy(false);
    }
  };

  const runDemo = async () => {
    if (!context.customer_id) return;
    setBusy(true);
    try {
      const res = await api.post(`/api/autonomous-pipeline/runs/${context.customer_id}`, {
        mode: 'demo',
        stealth: true,
      });
      toast.success(`Demo pipeline ${res.run_id?.slice(0, 8)}…`);
      setLatest(res.run);
    } catch (e) {
      toast.error(e.message || 'Run failed');
    } finally {
      setBusy(false);
    }
  };

  const approve = async (phaseId) => {
    if (!context.customer_id) return;
    setBusy(true);
    try {
      await api.post(`/api/autonomous-pipeline/runs/${context.customer_id}/approve`, {
        phase_id: phaseId,
        note: 'Human approval via AERTA UI',
      });
      toast.success(`Recorded approval for ${phaseId}`);
      await load();
    } catch (e) {
      toast.error(e.message || 'Approve failed');
    } finally {
      setBusy(false);
    }
  };

  const executeCodefix = async () => {
    if (!context.customer_id) return;
    setBusy(true);
    try {
      const res = await api.post(`/api/autonomous-pipeline/runs/${context.customer_id}/codefix/execute`, {
        title: codefixTitle || undefined,
        summary: codefixSummary || undefined,
      });
      toast.success(
        <div className="space-y-1">
          <div>CodeFix PR created</div>
          {res.pr_url ? (
            <a className="text-xs text-primary underline" href={res.pr_url} target="_blank" rel="noreferrer">
              Open PR
            </a>
          ) : null}
        </div>
      );
      await load();
    } catch (e) {
      toast.error(e.message || 'CodeFix execute failed');
    } finally {
      setBusy(false);
    }
  };

  async function downloadContractSnapshot() {
    if (!context.customer_id || !latest?.run_id) return;
    const customerId = context.customer_id;
    const scenarioId = latest.lineage?.scenario_id || latest.scenario_id;
    const params = new URLSearchParams();
    if (scenarioId) params.set('scenario_id', String(scenarioId));
    const qs = params.toString();
    const path = `/api/run-intelligence/${encodeURIComponent(customerId)}/snapshot/${encodeURIComponent(latest.run_id)}${qs ? `?${qs}` : ''}`;
    setSnapshotBusy(true);
    try {
      const data = await api.get(path, { timeoutMs: 60000 });
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `aerta-run-contract-${latest.run_id}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setSnapshotBusy(false);
    }
  }

  if (pilotLoading || configLoading) {
    return (
      <div className="space-y-6">
        <h1 className="font-heading text-3xl font-bold text-foreground">{PIPELINE_PAGE_TITLE}</h1>
        <div className="flex min-h-[12rem] flex-col items-center justify-center gap-3 rounded-lg border border-border bg-secondary/20 px-4 py-10">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-sm text-muted-foreground">Loading pipeline configuration…</p>
        </div>
      </div>
    );
  }

  if (config === null) {
    return (
      <div className="space-y-6">
        <h1 className="font-heading text-3xl font-bold text-foreground">{PIPELINE_PAGE_TITLE}</h1>
        <p className="text-muted-foreground">
          Could not load pipeline configuration. Check that the API is reachable, then try again.
        </p>
        <Button type="button" variant="outline" onClick={() => load()}>
          Retry
        </Button>
      </div>
    );
  }

  if (!config.enabled) {
    return (
      <div className="space-y-6">
        <h1 className="font-heading text-3xl font-bold text-foreground">{PIPELINE_PAGE_TITLE}</h1>
        <p className="text-muted-foreground">This feature is disabled on the server.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <Workflow className="mt-1 h-10 w-10 text-primary" />
          <div>
            <h1 className="font-heading text-3xl font-bold text-foreground">{PIPELINE_PAGE_TITLE}</h1>
            <p className="max-w-3xl text-muted-foreground">
              The API route is <code className="rounded bg-secondary px-1">/autonomous-pipeline</code> — Red AI
              orchestration for six-phase reconnaissance, offensive validation (authorized scope), CypherFix triage,
              and CodeFix. Set <code className="rounded bg-secondary px-1">AERTAGO_SUPERVISED_MODE=true</code> on the
              server to emphasize human approval gates; default exploration leaves that flag off. Default run mode is{' '}
              <strong>engagement</strong>: findings come from your real Layer 1 risk report (discovery + risk scan). Use{' '}
              <strong>demo</strong> only for synthetic placeholder data.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={runEngagement} disabled={busy || !context.customer_id}>
            Run engagement
          </Button>
          <Button variant="outline" onClick={runDemo} disabled={busy || !context.customer_id}>
            Run demo (synthetic)
          </Button>
        </div>
      </div>

      <section className="space-y-3" aria-labelledby="pipeline-bento-heading">
        <h2
          id="pipeline-bento-heading"
          className="font-heading text-lg font-semibold tracking-tight text-foreground"
        >
          Pipeline at a glance
        </h2>
        <MagicBento
          cards={PIPELINE_BENTO_CARDS}
          glowColor={PIPELINE_BENTO_GLOW}
          spotlightRadius={380}
        />
      </section>

      <Card className="border-amber-500/40 bg-amber-500/5">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
            <ShieldAlert className="h-5 w-5" />
            Legal &amp; safety
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <p>{config.legal_disclaimer}</p>
          <p>
            Dangerous or active tooling (Kali, MCP sandboxes, stealth vs full modes) must stay under your
            organization&apos;s policies. AERTA records approvals and artifacts; it does not launch exploits
            or open pull requests unless explicitly enabled on the server.
          </p>
        </CardContent>
      </Card>

      <Card className="border-primary/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Terminal className="h-5 w-5" />
            Allowlisted executor (nmap via AERTAX Kali bridge)
          </CardTitle>
          <CardDescription>
            Runs only on the API host (or Docker socket on Linux). Targets must match{' '}
            <code className="rounded bg-secondary px-1">AERTA_SCAN_ALLOWLIST</code> — same as network scan.
            Enable <code className="rounded bg-secondary px-1">AERTA_PENTEST_EXECUTOR_ENABLED</code> and{' '}
            <code className="rounded bg-secondary px-1">AERTA_SCAN_ENABLED</code> on the server.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <Badge variant={execInfo?.executor_enabled ? 'default' : 'secondary'}>
              Executor {execInfo?.executor_enabled ? 'on' : 'off'}
            </Badge>
            {execInfo?.execution_backend && (
              <Badge variant="outline">backend: {execInfo.execution_backend}</Badge>
            )}
            {!kaliStatus?.bridge_configured ? (
              <Badge variant="destructive" title={kaliStatus?.remediation || kaliStatus?.bridge_error || ''}>
                AERTAX bridge DOWN — CRITICAL
              </Badge>
            ) : (
              <Badge variant={kaliStatus?.bridge_healthy ? 'default' : 'destructive'}>
                {kaliStatus?.bridge_healthy
                  ? 'AERTAX bridge up'
                  : 'AERTAX bridge DOWN — CRITICAL'}
              </Badge>
            )}
            {execInfo?.docker_kali && (
              <Badge variant="outline">Docker Kali: {execInfo.kali_image}</Badge>
            )}
          </div>
          <div className="flex max-w-xl flex-col gap-2 sm:flex-row sm:items-end">
            <div className="flex-1 space-y-1">
              <label className="text-xs text-muted-foreground">Target (single IP/CIDR within allowlist)</label>
              <Input
                value={execTarget}
                onChange={(e) => setExecTarget(e.target.value)}
                placeholder="10.0.0.5"
                data-testid="executor-target"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Profile</label>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={execProfile}
                onChange={(e) => setExecProfile(e.target.value)}
              >
                {(execInfo?.profiles || [{ id: 'nmap_tcp_default' }, { id: 'nmap_top100_sv' }]).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.title || p.id}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={execAutoRisk}
              onChange={(e) => setExecAutoRisk(e.target.checked)}
            />
            Auto Layer 1 risk scan after ingest (background)
          </label>
          <Button
            type="button"
            variant="secondary"
            disabled={
              busy ||
              !context.customer_id ||
              !execTarget.trim() ||
              !execInfo?.executor_enabled ||
              allowlistBlocksExecutor
            }
            onClick={runAllowlistedScan}
          >
            Run allowlisted scan
          </Button>
          {!execInfo?.executor_enabled && (
            <p className="text-xs text-muted-foreground">
              Executor disabled in API env — set{' '}
              <code className="rounded bg-secondary px-1">AERTA_PENTEST_EXECUTOR_ENABLED=true</code>,{' '}
              <code className="rounded bg-secondary px-1">AERTA_SCAN_ENABLED=true</code>,{' '}
              <code className="rounded bg-secondary px-1">AERTA_KALI_BRIDGE_URL</code> +{' '}
              <code className="rounded bg-secondary px-1">AERTA_KALI_BRIDGE_TOKEN</code>, and a reachable AERTAX
              bridge; redeploy.
            </p>
          )}
          {execInfo?.executor_enabled && allowlistBlocksExecutor && (
            <p className="text-xs text-destructive">
              Allowlist gate closed — set <code className="rounded bg-secondary px-1">AERTA_SCAN_ALLOWLIST</code> (or
              lab-only <code className="rounded bg-secondary px-1">AERTA_SCAN_ALLOWLIST_REQUIRED=false</code>) so
              targets match policy.
            </p>
          )}
          {execInfo?.executor_enabled && context.kali_bridge_configured === false && (
            <p className="text-xs text-muted-foreground">
              Bridge URL/token not both set on the API — executor cannot reach AERTAX.
            </p>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Graph</CardTitle>
            <CardDescription>Neo4j findings sync</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={config.neo4j_configured ? 'default' : 'secondary'}>
                {config.neo4j_configured ? 'URI configured' : 'Not configured'}
              </Badge>
              {graphSummary?.configured && (
                <Badge variant="outline">
                  {typeof graphSummary.total === 'number' ? `${graphSummary.total} Finding node(s)` : 'Live'}
                </Badge>
              )}
            </div>
            {graphSummary?.error && (
              <p className="text-xs text-amber-600">{graphSummary.error}</p>
            )}
            <p className="text-xs text-muted-foreground">
              Enable Neo4j with Docker profile <code className="rounded bg-secondary px-1">graph</code> and{' '}
              <code className="rounded bg-secondary px-1">NEO4J_URI</code>. Pipeline runs sync :Finding nodes.
            </p>
            <Link
              to="/graph-findings"
              className="inline-block text-xs font-medium text-primary underline"
            >
              View graph findings in AERTA →
            </Link>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <Network className="h-4 w-4" />
              Dojo / red-blue
            </CardTitle>
            <CardDescription>Orchestrator artifacts</CardDescription>
          </CardHeader>
          <CardContent className="text-sm">
            {dojo ? (
              <ul className="space-y-1 text-muted-foreground">
                <li>Match results: {dojo.match_results_exists ? 'present' : 'missing'}</li>
                <li>Current match: {dojo.current_match_exists ? 'present' : 'missing'}</li>
              </ul>
            ) : (
              <p className="text-muted-foreground">Could not load status.</p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <GitBranch className="h-4 w-4" />
              CodeFix PR
            </CardTitle>
            <CardDescription>GitHub integration</CardDescription>
          </CardHeader>
          <CardContent>
            <Badge variant={config.codefix_ready ? 'default' : 'secondary'}>
              {config.codefix_ready ? 'Ready' : 'Not ready'}
            </Badge>
            {!config.codefix_ready && config.codefix_reason ? (
              <p className="mt-2 text-xs text-muted-foreground">{config.codefix_reason}</p>
            ) : null}
            {config.codefix_repo ? (
              <p className="mt-2 text-xs text-muted-foreground">
                Repo: <code className="rounded bg-secondary px-1">{config.codefix_repo}</code>
              </p>
            ) : null}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Phases</CardTitle>
          <CardDescription>Latest run or template phases from config.</CardDescription>
        </CardHeader>
        <CardContent>
          <ol className="list-decimal space-y-2 pl-5 text-sm">
            {(latest?.phases || config.phases || []).map((p) => (
              <li key={p.id || p.title} className="text-foreground">
                <span className="font-medium">{p.title || p.id}</span>
                {p.status && (
                  <Badge variant="outline" className="ml-2">
                    {p.status}
                  </Badge>
                )}
                {(p.id === 'offensive' || p.id === 'codefix') && p.status === 'awaiting_approval' && (
                  <Button size="sm" variant="outline" className="ml-2" disabled={busy} onClick={() => approve(p.id)}>
                    Record approval
                  </Button>
                )}
              </li>
            ))}
          </ol>
        </CardContent>
      </Card>

      {latest && (
        <Card>
          <CardHeader>
            <CardTitle className="flex flex-wrap items-center justify-between gap-2">
              <span>Latest run</span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={snapshotBusy || !latest?.run_id}
                onClick={() => void downloadContractSnapshot()}
              >
                {snapshotBusy ? 'Exporting…' : 'Export contract snapshot'}
              </Button>
            </CardTitle>
            <CardDescription className="font-mono text-xs break-all">{latest.run_id}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="rounded-md border border-border/60 p-3">
              <p className="mb-2 text-sm font-medium">Execute CodeFix PR</p>
              <div className="grid gap-2">
                <Input
                  placeholder="PR title (optional)"
                  value={codefixTitle}
                  onChange={(e) => setCodefixTitle(e.target.value)}
                />
                <Input
                  placeholder="Short summary (optional)"
                  value={codefixSummary}
                  onChange={(e) => setCodefixSummary(e.target.value)}
                />
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  size="sm"
                  onClick={executeCodefix}
                  disabled={
                    busy ||
                    !config.codefix_ready ||
                    !latest?.phases?.some((p) => p.id === 'codefix' && (p.status === 'approved' || p.status === 'completed'))
                  }
                >
                  Execute CodeFix PR
                </Button>
                {latest?.codefix_result?.pr_url ? (
                  <a href={latest.codefix_result.pr_url} target="_blank" rel="noreferrer" className="text-xs text-primary underline">
                    Open latest PR
                  </a>
                ) : null}
              </div>
            </div>
            <pre className="max-h-96 overflow-auto rounded-md bg-secondary/40 p-3 text-xs">
              {JSON.stringify(latest, null, 2)}
            </pre>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
